import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useChatWidgetState
 * ---------------------------------------------------------------------------
 * Owns the state machine for the ChatBiz AI visitor widget launcher.
 *
 * States
 *  - "closed"   launcher bubble only, widget panel unmounted/hidden
 *  - "open"     widget panel visible and interactive
 *  - "notified" launcher bubble with an unread badge (widget still closed)
 *
 * Transitions
 *  closed   --click launcher-->            open
 *  open     --click launcher / close (X)--> closed
 *  open     --Escape key-->                closed
 *  open     --click outside panel-->       closed
 *  closed   --new proactive/agent message--> notified
 *  notified --click launcher-->            open (badge clears)
 *
 * The hook is intentionally transport-agnostic: it does not know about
 * sockets or REST calls. Call `notify()` whenever an inbound message
 * arrives while the widget is closed, and the state machine will move to
 * "notified" and track the unread count for the launcher badge.
 * ---------------------------------------------------------------------------
 */
export default function useChatWidgetState({ startOpen = false } = {}) {
  const [status, setStatus] = useState(startOpen ? "open" : "closed");
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef(null);
  const launcherRef = useRef(null);

  const isOpen = status === "open";

  const open = useCallback(() => {
    setStatus("open");
    setUnreadCount(0);
  }, []);

  const close = useCallback(() => {
    setStatus((prev) => (prev === "open" ? "closed" : prev));
  }, []);

  const toggle = useCallback(() => {
    setStatus((prev) => {
      if (prev === "open") return "closed";
      setUnreadCount(0);
      return "open";
    });
  }, []);

  const notify = useCallback((incrementBy = 1) => {
    setStatus((prev) => (prev === "open" ? "open" : "notified"));
    setUnreadCount((prev) => (status === "open" ? 0 : prev + incrementBy));
  }, [status]);

  // Close on Escape key while open
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close]);

  // Close on click/tap outside the panel and launcher
  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (e) => {
      const panel = panelRef.current;
      const launcher = launcherRef.current;
      if (!panel) return;
      if (panel.contains(e.target)) return;
      if (launcher && launcher.contains(e.target)) return;
      close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [isOpen, close]);

  // Move focus into the panel when it opens (a11y)
  useEffect(() => {
    if (isOpen && panelRef.current) {
      const focusable = panelRef.current.querySelector(
        "input, textarea, button"
      );
      if (focusable) focusable.focus();
    }
  }, [isOpen]);

  return {
    status, // "closed" | "open" | "notified"
    isOpen,
    unreadCount,
    open,
    close,
    toggle,
    notify,
    panelRef,
    launcherRef,
  };
}
